import { useEffect, useRef, useState } from 'react';
import { BellIcon } from './mailNotifyIcons';
import MailNotifyErrorNotice from './MailNotifyErrorNotice';
import { MOBILE_HINT as CONNECTOR_MOBILE_HINT } from './OutlookConnectorCard';
import { restoreMailNotify, setMailNotifyPhone } from '../utils/api';
import { openSettings } from '../utils/settingsModal';
import {
  FALLBACK,
  NO_ENABLED_RULE_HINT,
  POLL_TIMEOUT_SUBTITLE,
  STATE_CLASS,
  STATE_LABEL,
  SYNCING_LABEL,
  SYNCING_SUBTITLE,
  canRestore,
  isOutlookReason,
  stateSubtitle,
  lastOperationError,
  loadErrorText,
} from './mailNotifyStatus';
import './MailNotifyCard.css';
import './MailRuleSection.css';

/**
 * 送出完成後按鈕再鎖這麼久（沿用 D-1007-13）：完成那一刻畫面會變，連點的第二下不該落在新的按鈕上。
 */
const COOLDOWN_MS = 800;

/**
 * 電話通知本版不開放：MyAgent 還沒有電話語音通道，整列先藏起來。程式留著，下一版開放時改成 true 即可。
 */
const PHONE_NOTIFY_ENABLED = false;

/**
 * 「Outlook即時通知」狀態卡（新架構，SA v2.0 C04～C07、C06a；US-NTF-01／02／03）。
 *
 * ⛔ 沒有訂閱／解除訂閱（D-1013-01）：存規則就是訂閱，有啟用中的規則 MyAgent 就自動建立 Graph 訂閱。
 * 這張卡只講現況；需要使用者處理時（已暫停、L1／L2 完成前的已中斷）才給「恢復通知」。
 *
 * 版面、徽章、按鈕與告知條樣式在 MailNotifyCard.css（與 Outlook 卡、錯誤告知共用）。
 * 手機版與電腦版相同（A-08），只有 Outlook 相關原因時把「前往設定」換成灰框，因為重新連結只能在網頁版做。
 *
 * @param {ReturnType<import('./useMailNotify').useMailNotify>} mailNotify 頁面共用的資料來源
 * @param {null|'linked'|'unlinked'|'expired'|'unknown'} outlookLink Outlook 連結狀態（不是已連結時「恢復通知」鎖住）
 * @param {boolean} [checkingLink] Outlook 連結狀態重查中（沿用上次的「已連結」）：「恢復通知」「前往設定」先不出現，查回來才決定（AGUI-1273）
 * @param {boolean} [showPhone] 規則區顯示中（Outlook 已連結）才放電話通知列（SA §1.3）
 * @param {boolean} [autoRebuild] MyAgent 已完成過期自動重建（L1／L2）時，已中斷不給「恢復通知」
 */
export default function MailNotifyStatusCard({
  mailNotify, outlookLink, checkingLink = false, isMobile = false, showPhone = false, autoRebuild = false,
}) {
  const { data, syncing, loadError, loadErrorDetail, busy, pollTimedOut, reportedErrorCode, refresh, write } = mailNotify;
  const [restoring, setRestoring] = useState(false);
  const [savingPhone, setSavingPhone] = useState(false);
  const [coolingDown, setCoolingDown] = useState(false);
  const [error, setError] = useState(null);
  // ⛔ 重複點擊用 ref 同步擋：連點的兩下可能落在同一次重新渲染之前（AGUI-1215）
  const lockRef = useRef(false);
  const cooldownTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(cooldownTimerRef.current), []);

  // 處理中（剛收到 202、或 MyAgent 正在套用）一律顯示「處理中」：202 之後第一次輪詢回來前，data 還是舊狀態
  const loadedState = syncing || loadError ? null : data?.state;
  const state = loadedState && busy ? 'PENDING' : loadedState;
  const reason = data?.reason || null;
  const rules = data?.rules || [];
  const hasEnabledRule = rules.some((r) => r.enabled);
  // AGUI-1273：切到本頁或重新整理時，Outlook 連結還在重查就先不給「恢復通知」，比照規則區（AGUI-1259）
  const restorable = Boolean(state) && !checkingLink && canRestore(state, autoRebuild);
  const outlookIssue = state === 'NEEDS_REAUTH' && isOutlookReason(reason);
  const notify = data?.notify ?? null;

  const runLocked = async (setRunning, request, fallback) => {
    if (lockRef.current) return;
    lockRef.current = true;
    setRunning(true);
    setError(null);
    const result = await write(request, { fallback });
    setRunning(false);
    // 逾時沒有錯誤可講：副標題已換成「連線逾時…」
    if (!result.ok && result.error) setError(result.error);
    setCoolingDown(true);
    cooldownTimerRef.current = setTimeout(() => {
      lockRef.current = false;
      setCoolingDown(false);
    }, COOLDOWN_MS);
  };

  const handleRestore = () => runLocked(setRestoring, (v) => restoreMailNotify(v), FALLBACK.setting);
  // 電話開關不需二次確認，送出期間 disabled；失敗時開關停在原位（畫面以重讀後的 notify 為準）
  const handlePhone = () => runLocked(setSavingPhone, (v) => setMailNotifyPhone(!notify.phone, v), FALLBACK.setting);

  let subtitle = stateSubtitle(state, reason, autoRebuild);
  if (syncing) subtitle = SYNCING_SUBTITLE;
  else if (loadError) subtitle = loadErrorText(loadErrorDetail);
  else if (pollTimedOut) subtitle = POLL_TIMEOUT_SUBTITLE;

  const restoreDisabled = restoring || coolingDown || busy || !hasEnabledRule || outlookLink !== 'linked';

  return (
    <div className="mail-alert-task mail-notify-status-card">
      <div className="mail-alert-task__row">
        <div className="mail-alert-task__icon">
          <BellIcon />
        </div>

        <div className="mail-alert-task__body">
          <div className="mail-alert-task__title-row">
            <span className="mail-alert-task__title">Outlook即時通知</span>
            {syncing ? (
              <span className="mail-alert-task__state mail-alert-task__state--pending">{SYNCING_LABEL}</span>
            ) : (
              state && <span className={`mail-alert-task__state ${STATE_CLASS[state]}`}>{STATE_LABEL[state]}</span>
            )}
          </div>
          {/* 讀取失敗時沒有徽章，說明行是唯一的警示，改紅字（FRD AC-NTF-01-8、SA C06；10-19 使用者裁決）。
              已暫停／已中斷／異常維持灰字，紅色只在徽章（10-19 使用者裁決，依 SA C04） */}
          <span
            className={`mail-alert-task__subtitle${loadError && !syncing ? ' mail-alert-task__subtitle--error' : ''}`}
            role={loadError && !syncing ? 'alert' : undefined}
          >
            {subtitle}
          </span>
        </div>

        {/* 按鈕區在沒有按鈕時也佔位：狀態切換時標題與徽章的位置不動 */}
        <div className="connector-pill-group">
          {loadError && !syncing && (
            <button type="button" className="connector-pill" onClick={refresh}>
              重新整理
            </button>
          )}
          {restorable && outlookIssue && !isMobile && (
            <button type="button" className="connector-pill" onClick={() => openSettings('connector')}>
              前往設定
            </button>
          )}
          {restorable && (
            <button
              type="button"
              className="connector-pill connector-pill--reconnect"
              disabled={restoreDisabled}
              onClick={handleRestore}
            >
              {restoring ? '恢復中...' : '恢復通知'}
            </button>
          )}
        </div>
      </div>

      {restorable && !hasEnabledRule && (
        <div className="mail-alert-task__notice mail-alert-task__notice--info" role="status">
          <span className="mail-alert-task__notice-text">{NO_ENABLED_RULE_HINT}</span>
        </div>
      )}

      {restorable && outlookIssue && isMobile && (
        <div className="mail-alert-task__notice mail-alert-task__notice--info">
          <span className="mail-alert-task__notice-text">{CONNECTOR_MOBILE_HINT}</span>
        </div>
      )}

      {/*
        這個分頁剛操作的錯誤優先；沒有時顯示讀回來的「上一次操作失敗」（FUNC-030～035），處理中或讀取失敗時不顯示。
        同一個錯誤已在動作現場報過（例如規則區的刪除失敗）就不再重複（UAT 10-21）
      */}
      <MailNotifyErrorNotice
        error={error || (!syncing && !loadError && !busy ? lastOperationError(data?.operation, reportedErrorCode) : null)}
        isMobile={isMobile}
      />

      {/* 電話通知（C06a）：/settings 還沒做好時 AP 回 notify=null，整列不出現〔MyAgent 待實作 S2〕 */}
      {PHONE_NOTIFY_ENABLED && showPhone && notify && !syncing && !loadError && (
        <div className="mail-notify-status-card__phone">
          <div className="mail-notify-status-card__phone-text">
            <span className="mail-notify-status-card__phone-title">電話通知</span>
            <span className="mail-notify-status-card__phone-desc">
              命中規則時，另以電話語音提醒你
              {/* 括號整段不拆開：欄寬不夠時換行落在括號前，不會剩「開啟）」掛在第二行 */}
              <span className="mail-notify-status-card__nowrap">（M+ 訊息固定開啟）</span>
            </span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={Boolean(notify.phone)}
            aria-label="電話通知"
            className="mail-rule-section__switch"
            disabled={savingPhone || coolingDown || busy}
            onClick={handlePhone}
          />
        </div>
      )}
    </div>
  );
}
